import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  getAccessStatusForVisitorGroup,
  getAllLockGroupsAssignedToVisitorGroup,
  getAllLocksAssignedDirectlyToVisitorGroup,
  getAmountOfMembersInGroup,
  getScheduleDaysForVisitorGroupGroup,
  getScheduleTimeForGroup,
  isOfficeModeEnabledForVisitorGroup,
  isScheduleSetUpForVisitorGroup
} from 'utils/VisitorGroup/VisitorGroup';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import DevicesAndGroupsCell from 'v2/components/DevicesAndGroupsCell/DevicesAndGroupsCell';
import { DevicesAndGroupsColumnHeader } from 'v2/components/DevicesAndGroupsColumnHeader/DevicesAndGroupsColumnHeader';
import { OriginCell } from 'v2/components/OriginCell/OriginCell';
import { OriginColumnHeader } from 'v2/components/OriginColumnHeader/OriginColumnHeader';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { TextCell, TwoLineCell } from 'v2/components/TextCell/TextCell';
import { WarningCell } from 'v2/components/Warning/Warning';
import { DATAGRID_DEFAULT_PAGE_SIZE, DATAGRID_PAGE_SIZE_OPTIONS, DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { getUserGroupPath } from 'v2/paths';
import { getVisitorGroupOriginLabel } from 'v2/utils/visitorGroupOrigin';

interface Props {
  visitorGroups: VisitorGroup[];
  loading: boolean;
}

const GroupsOverviewTable: React.FC<Props> = ({ visitorGroups, loading }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));

  const headerWithId = renderHeaderWithId();

  const rows = useMemo(
    () =>
      visitorGroups.map((visitorGroup) => ({
        id: visitorGroup.id,
        visitorGroup
      })),
    [visitorGroups, translation, theme]
  );

  const columns: GridColDef<(typeof rows)[number]>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: translation.name,
        sortable: true,
        flex: 2,
        renderHeader: headerWithId,
        valueGetter: (_, row) => row.visitorGroup.name,
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          return <TextCell variant="title">{visitorGroup.name}</TextCell>;
        }
      },
      {
        field: 'origin',
        headerName: translation.origin,
        sortable: true,
        width: 110,
        valueGetter: (_, row) => getVisitorGroupOriginLabel(row.visitorGroup, translation),
        renderHeader: renderHeaderWithId(undefined, () => <OriginColumnHeader variant="group" />),
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          return <OriginCell label={getVisitorGroupOriginLabel(visitorGroup, translation)} />;
        }
      },
      {
        field: 'amount',
        headerName: translation.users,
        sortable: true,
        flex: 1,
        renderHeader: renderHeaderWithId(undefined, () => (
          <ColumnHeaderWithTooltip
            label={translation.users}
            tooltip={<Typography variant="caption">{translation.atlas_groups_max_users_tooltip}</Typography>}
          />
        )),
        valueGetter: (_, row) => getAmountOfMembersInGroup(row.visitorGroup),
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          const amountOfMembersInGroup = getAmountOfMembersInGroup(visitorGroup);
          const maxNumberOfMembersInGroup = visitorGroup.maxNumberOfMembers;
          if (amountOfMembersInGroup)
            return maxNumberOfMembersInGroup ? (
              <TextCell>
                {amountOfMembersInGroup}/{maxNumberOfMembersInGroup}
              </TextCell>
            ) : (
              <TextCell>{amountOfMembersInGroup}</TextCell>
            );
          else {
            return <WarningCell label={translation.no_users} />;
          }
        }
      },
      {
        field: 'schedule',
        headerName: translation.schedule,
        sortable: false,
        minWidth: 130,
        flex: 1,
        renderHeader: renderHeaderWithId(undefined, () => (
          <ColumnHeaderWithTooltip
            label={translation.schedule}
            tooltip={<Typography variant="caption">{translation.schedule_column_tooltip}</Typography>}
          />
        )),
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          if (isScheduleSetUpForVisitorGroup(visitorGroup)) {
            return (
              <TwoLineCell
                title={getScheduleDaysForVisitorGroupGroup(visitorGroup, translation)}
                content={getScheduleTimeForGroup(visitorGroup)}
              />
            );
          } else {
            return <WarningCell label={translation.no_schedule} />;
          }
        }
      },
      {
        field: 'device_groups',
        headerName: translation.device_groups_column_header,
        sortable: false,
        minWidth: 170,
        flex: 1,
        renderHeader: renderHeaderWithId(undefined, () => <DevicesAndGroupsColumnHeader />),
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          const devicesCount = getAllLocksAssignedDirectlyToVisitorGroup(visitorGroup).length;
          const groupsCount = getAllLockGroupsAssignedToVisitorGroup(visitorGroup).length;
          return <DevicesAndGroupsCell devicesCount={devicesCount} groupsCount={groupsCount} />;
        }
      },
      {
        field: 'office_mode',
        headerName: translation.office_mode,
        sortable: true,
        minWidth: 160,
        renderHeader: renderHeaderWithId(undefined, () => (
          <ColumnHeaderWithTooltip
            label={translation.office_mode}
            tooltip={
              <Stack spacing={1}>
                <Typography variant="caption">{translation.office_mode_tooltip_description}</Typography>
                <Typography variant="caption">{translation.office_mode_tooltip_permission}</Typography>
              </Stack>
            }
          />
        )),
        valueGetter: (_, row) => isOfficeModeEnabledForVisitorGroup(row.visitorGroup),
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          return (
            <TextCell>{isOfficeModeEnabledForVisitorGroup(visitorGroup) ? translation.yes : translation.no}</TextCell>
          );
        }
      },
      {
        field: 'access',
        headerName: translation.access,
        sortable: true,
        width: isExtraLargeScreen ? 140 : 120,
        renderHeader: renderHeaderWithId(undefined, () => (
          <ColumnHeaderWithTooltip
            label={translation.access}
            tooltip={<Typography variant="caption">{translation.access_column_tooltip}</Typography>}
          />
        )),
        valueGetter: (_, row) => getAccessStatusForVisitorGroup(row.visitorGroup),
        renderCell: (params) => {
          const { visitorGroup } = params.row;
          const complete = getAccessStatusForVisitorGroup(visitorGroup);
          return (
            <StatusChip
              variant={complete ? 'complete' : 'incomplete'}
              label={complete ? translation.complete : translation.incomplete}
              iconOnly={!isExtraLargeScreen}
            />
          );
        }
      }
    ],
    [rows, translation, headerWithId, isExtraLargeScreen]
  );

  const columnVisibilityModel = {
    name: true,
    origin: isLargeScreen,
    amount: isMediumScreen,
    schedule: isLargeScreen,
    device_groups: isMediumScreen,
    office_mode: isExtraLargeScreen,
    access: true
  };

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      slots={dataGridIdSlots}
      autoHeight
      hideFooter={rows.length <= DATAGRID_DEFAULT_PAGE_SIZE}
      pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
      rowHeight={DATAGRID_ROW_HEIGHT}
      loading={loading}
      initialState={{
        sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        pagination: { paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE } }
      }}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton'
        }
      }}
      onRowClick={({ id }) => navigate(getUserGroupPath(String(id)))}
      disableRowSelectionOnClick
      disableColumnSelector
      disableRowSelectionExcludeModel
      disableColumnFilter
      disableColumnMenu
    />
  );
};

export default GroupsOverviewTable;
